import React, { useEffect, useState } from "react";
import { Container, Card, CardTitle, CardBody, Row, Col, Table, Button, Spinner, Badge } from "reactstrap";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { getBulkScheduleSummary } from "../../../../helpers/api_rombo_helper";
import BreadcrumbLink from "../../../../components/Common/BreadcrumbLink";
import PaginationV2 from "../../../../components/Pagination/PaginationV2";
import i18n from "../../../../i18n";
import { orgId } from "../../../../constants/utils";

const selectToken = createSelector((state) => state.Login, (l) => l.token);

const BulkTaskList = () => {
  document.title = `${i18n.t("MassProgrammingHistory")} - ${orgId}`;
  const token = useSelector(selectToken);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 15;

  useEffect(() => {
    if (!token) return;
    getBulkScheduleSummary(token, "list", "json")
      .then((res) => {
        const items = Array.isArray(res) ? res : res?.items ?? [];
        setTasks(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const handleDownload = async (task) => {
    setDownloadingId(task.document_id);
    try {
      console.log("Original file_summary:", task.file_summary);

      const publicUrl = task.file_summary.replace(
        "s3://requests.services.stg/",
        "https://requests.services.stg.s3.amazonaws.com/"
      );

      console.log("Generated URL:", publicUrl);

      window.open(publicUrl,"_blank");
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return { date: "—", time: "—" };
    const [date, time] = value.split(" ");
    return { date, time };
  };

  const renderStatusBadge = (status) => {
    if (status === "COMPLETED") {
      return <Badge color="success">{i18n.t("Completed")}</Badge>;
    }

    if (status === "PENDING") {
      return <Badge color="warning" className="text-white">{i18n.t("Pending")}</Badge>;
    }

    return <Badge color="secondary">{status}</Badge>;
  };

  const paginatedTasks = tasks.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const actionButtonStyle = {
    width: "30px",
    height: "30px",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  };

  const handleDownloadTemplate = () => {
    try {
      window.open(
        "https://template.bulk.s3.us-west-1.amazonaws.com/rombo_bulk.xlsx",
        "_blank"
      );
    } catch (error) {
      console.error("Error downloading template:", error);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadcrumbLink title={i18n.t("Services")} link="/services/request" breadcrumbItem={i18n.t("MassiveServiceLoad")} />
          </Col>
        </Row>
        <Card>
          <CardBody>
            <Row className="mb-2">
              <Col>
                <CardTitle className="h4">{i18n.t("Services")}</CardTitle>
              </Col>
              <Col className="text-end">
              <div className="d-flex justify-content-end gap-2">
                <Link to="/services/bulk-schedule">
                  <Button className="btn btn-request" size="sm">
                    {i18n.t("MassiveServiceLoad")}
                  </Button>
                </Link>
                <Button
                  className="btn btn-import-coke d-flex align-items-center justify-content-center"
                  onClick={handleDownloadTemplate}
                >
                  <i
                    className="mdi mdi-download"
                    style={{
                      color: "#5F74E7",
                      fontSize: 16,
                      lineHeight: 1
                    }}
                  />
                </Button>
              </div>
            </Col>
            </Row>
            <div className="orange-separator" />
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <>
                <Table className="align-middle dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline table-users">
                  <thead className="table-light">
                    <tr>
                      <th className="name-column">{i18n.t("Company")}</th>
                      <th>{i18n.t("Folio")}</th>
                      <th>{i18n.t("RequestedBy")}</th>
                      <th>{i18n.t("Date")}</th>
                      <th>{i18n.t("Hour")}</th>
                      <th className="text-center">Vehículos solicitados</th>
                      <th className="text-center">Pasajeros totales</th>
                      <th className="text-center">{i18n.t("Status")}</th>
                      <th className="text-center">{i18n.t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center text-muted py-4">
                          Sin registros
                        </td>
                      </tr>
                    ) : (
                      paginatedTasks.map((t) => {
                        const { date, time } = formatDateTime(t.created_at);

                        return (
                          <tr key={t.document_id}>
                            <td>{t.company_name ?? "Cliente no identificado"}</td>
                            <td>{t.document_id ?? "Desconocido"}</td>
                            <td style={{ fontSize: 13 }}>{t.created_by ?? "Usuario no identificado"}</td>
                            <td className="text-center">{date}</td>
                            <td className="text-center">{time}</td>
                            <td className="text-center">{t.total_vehicles ?? "—"}</td>
                            <td className="text-center">{t.total_rows}</td>
                            <td>{renderStatusBadge(t.status)}</td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <Link to={`/services/bulk-schedule/tasks/${t.document_id}`}>
                                  <Button title="Ver detalle" style={{ ...actionButtonStyle, backgroundColor: "#EEF0FC", borderColor: "#EEF0FC" }}>
                                    <i className="mdi mdi-eye" style={{ color: "#5F74E7" }} />
                                  </Button>
                                </Link>
                                <Button
                                  title="Descargar resumen"
                                  disabled={downloadingId === t.document_id}
                                  onClick={() => handleDownload(t)}
                                  style={{ ...actionButtonStyle, backgroundColor: "#FFE5D6", borderColor: "#FFE5D6" }}
                                >
                                  {downloadingId === t.document_id ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <i className="mdi mdi-file-excel-outline" style={{ color: "#FF5F00" }} />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
                <PaginationV2
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  totalItems={tasks.length}
                  onChangePage={setCurrentPage}
                  isLoading={loading}
                />
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default BulkTaskList;
